import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileSearch, FileUp, Lock, MessageSquareQuote, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/hooks/use-permissions';
import {
  deleteAdminQaItem,
  downloadAdminQaExport,
  fetchAdminQaItems,
} from '@/lib/api/admin';
import FileFormatMenu from '@/components/knowledge-base/file-format-menu';
import QaItemEditorDialog from '@/components/qa-base/qa-item-editor-dialog';
import QaImportDialog from '@/components/qa-base/qa-import-dialog';
import QaSearchDialog from '@/components/qa-base/qa-search-dialog';

const SECTION_BADGE_TONES = [
  'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
  'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  'bg-teal-500/15 text-teal-700 dark:text-teal-300',
  'bg-orange-500/15 text-orange-700 dark:text-orange-300',
];

function sectionTone(section) {
  const key = String(section || '');
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return SECTION_BADGE_TONES[h % SECTION_BADGE_TONES.length];
}

function getSourceId(item) {
  return String(item?.metadata?.sourceId || '').trim();
}

const EXPORT_HINTS = { xlsx: 'One sheet per category, with locks', md: 'The ### id text format' };
const IMPORT_HINTS = { xlsx: 'An edited export — new rows get IDs', md: 'The ### id text format' };

function QaBasePage() {
  // RBAC: knowledge:view renders the library read-only; manage unlocks add/import/edit.
  const canManage = useHasPermission('knowledge', 'manage');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState({ open: false, item: null });
  // format stays set while the dialog closes so its copy doesn't flip mid-animation.
  const [importDialog, setImportDialog] = useState({ open: false, format: 'xlsx' });
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const { items: nextItems, total: nextTotal } = await fetchAdminQaItems({ limit: 500 });
      setItems(nextItems);
      setTotal(nextTotal);
    } catch (error) {
      toast.error(`Failed to load phrases: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const haystack = [
        item.question,
        item.answer,
        item.section,
        getSourceId(item),
        ...(Array.isArray(item.aliases) ? item.aliases : []),
      ]
        .map((s) => String(s || '').toLowerCase())
        .join('\n');
      return haystack.includes(term);
    });
  }, [items, search]);

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const { filename } = await downloadAdminQaExport({ format });
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error(`Export failed: ${error.message || error}`);
    } finally {
      setIsExporting(false);
    }
  };

  const openImport = (format) => setImportDialog({ open: true, format });

  const handleDelete = async (item) => {
    const label = item.question || getSourceId(item) || 'phrase';
    if (!window.confirm(`Delete "${label}"? This permanently removes the phrase.`)) {
      return false;
    }
    try {
      await deleteAdminQaItem({ id: item.id });
      toast.success('Phrase deleted');
      refresh();
      return true;
    } catch (error) {
      toast.error(`Delete failed: ${error.message || error}`);
      return false;
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-6 p-6">
      <Card className="w-full shrink-0">
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="text-3xl flex items-center gap-3">
                <MessageSquareQuote className="h-7 w-7 text-primary" />
                Trigger Base
              </CardTitle>
              <p className="mt-2 text-sm text-muted-foreground">
                Curated trigger→reply phrases the assistant matches first; on a confident match it sends that reply
                (lightly adapted to context) instead of the normal flow. {total} phrase{total === 1 ? '' : 's'}.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={() => setSearchDialogOpen(true)}>
                <FileSearch className="mr-2 h-4 w-4" />
                Test a message
              </Button>
              <FileFormatMenu
                label="Export"
                icon={Download}
                busy={isExporting}
                hints={EXPORT_HINTS}
                onPick={handleExport}
              />
              {canManage && (
                <FileFormatMenu label="Import" icon={FileUp} hints={IMPORT_HINTS} onPick={openImport} />
              )}
              {canManage && (
                <Button type="button" onClick={() => setEditor({ open: true, item: null })}>
                  <Plus className="mr-2 h-4 w-4" />
                  New phrase
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Filter by trigger, reply, category, or source ID…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-md"
            />
            <Button type="button" variant="ghost" size="sm" onClick={refresh} disabled={isLoading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <ScrollArea className="min-h-0 flex-1">
            <ul className="divide-y divide-border">
              {isLoading && items.length === 0 && (
                <li className="space-y-3 p-5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ))}
                </li>
              )}

              {!isLoading && filteredItems.length === 0 && (
                <li className="flex flex-col items-center gap-3 p-12 text-center">
                  <MessageSquareQuote className="h-10 w-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {items.length === 0
                      ? 'No phrases yet — import an Excel or Markdown file, or add one by hand to get started.'
                      : 'No phrases match your filter.'}
                  </p>
                  {items.length === 0 && canManage && (
                    <FileFormatMenu
                      label="Import"
                      icon={FileUp}
                      hints={IMPORT_HINTS}
                      align="center"
                      onPick={openImport}
                    />
                  )}
                </li>
              )}

              {filteredItems.map((item) => (
                <QaItemRow
                  key={item.id}
                  item={item}
                  onEdit={canManage ? () => setEditor({ open: true, item }) : undefined}
                  onDelete={canManage ? () => handleDelete(item) : undefined}
                />
              ))}
            </ul>
          </ScrollArea>
        </CardContent>
      </Card>

      <QaItemEditorDialog
        open={editor.open}
        item={editor.item}
        onOpenChange={(open) => setEditor((cur) => ({ ...cur, open }))}
        onSaved={() => {
          setEditor({ open: false, item: null });
          refresh();
        }}
      />

      <QaImportDialog
        open={importDialog.open}
        format={importDialog.format}
        onOpenChange={(open) => setImportDialog((cur) => ({ ...cur, open }))}
        onApplied={() => {
          setImportDialog((cur) => ({ ...cur, open: false }));
          refresh();
        }}
      />

      <QaSearchDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        onEditRequest={
          canManage
            ? (item) => {
                setSearchDialogOpen(false);
                // Items from search carry the same id; switch into editor mode.
                setEditor({ open: true, item });
              }
            : undefined
        }
        onDeleteRequest={canManage ? handleDelete : undefined}
      />
    </div>
  );
}

function QaItemRow({ item, onEdit, onDelete }) {
  const sourceId = getSourceId(item);
  const aliases = Array.isArray(item.aliases) ? item.aliases : [];
  const section = item.section || 'Uncategorised';

  return (
    <li className="group flex items-start gap-4 px-5 py-4 hover:bg-muted/40">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', sectionTone(section))}>
            {section}
          </span>
          {sourceId && (
            <Badge variant="outline" className="font-mono text-[10px]">
              {sourceId}
            </Badge>
          )}
          {aliases.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              +{aliases.length} trigger{aliases.length === 1 ? '' : 's'}
            </Badge>
          )}
          {item.lockAfterAnswer && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-300 text-[10px] text-amber-700 dark:border-amber-700 dark:text-amber-400"
            >
              <Lock className="h-2.5 w-2.5" />
              Locks after reply
            </Badge>
          )}
        </div>
        <p className="text-sm font-semibold leading-snug">
          {item.question || (aliases[0] ?? <span className="italic text-muted-foreground">No trigger</span>)}
        </p>
        <p className="line-clamp-2 text-sm text-muted-foreground">{item.answer}</p>
      </div>
      {onEdit || onDelete ? (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {onEdit ? (
            <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
          ) : null}
          {onDelete ? (
            <Button type="button" variant="ghost" size="sm" onClick={onDelete} className="text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export default QaBasePage;
